// src/components/MemberDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { db, functions } from '../firebase';
import { doc, getDoc, Timestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { Alert, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPencilAlt,
    faTrashAlt,
    faUsers,
    faLayerGroup,
    faArrowLeft,
    faPhone,
    faEnvelope,
    faBirthdayCake,
    faCross,
    faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';

import { useAuth } from '../context/AuthContext';
import MemberPhotoAvatar from './MemberPhotoAvatar';
import FormattedPhoneNumber from './FormattedPhoneNumber';
import HistorySection from './HistorySection';

import './MemberDetail.css';

// Initialize Firebase Functions
const deleteMemberCallable = httpsCallable(functions, 'deleteMember');
const markMemberDeceasedCallable = httpsCallable(functions, 'markMemberDeceased');
const revertMemberToActiveCallable = httpsCallable(functions, 'revertMemberToActive');

// Helper: Format Birthday from individual fields (without year for privacy)
const formatBirthday = (member) => {
    if (!member || !member.birthdayMonth || !member.birthdayDay) {
        return null;
    }

    try {
        const month = parseInt(member.birthdayMonth, 10);
        const day = parseInt(member.birthdayDay, 10);
        // Use a placeholder year just for date formatting
        const date = new Date(2000, month - 1, day);

        if (isNaN(date.getTime())) {
            return null;
        }

        // Never show year for privacy
        return format(date, 'MMMM d');
    } catch (e) {
        console.error("Error formatting birthday from fields:", member, e);
        return null;
    }
};

// Helper: Format Passing Date from individual fields
const formatPassingDate = (member) => {
    if (!member || !member.passingMonth || !member.passingDay) {
        return null;
    }

    try {
        const month = parseInt(member.passingMonth, 10);
        const day = parseInt(member.passingDay, 10);
        const year = member.passingYear ? parseInt(member.passingYear, 10) : null;

        const date = new Date(year || 2000, month - 1, day);

        if (isNaN(date.getTime())) {
            return null;
        }

        // Show year if provided
        return year ? format(date, 'MMMM d, yyyy') : format(date, 'MMMM d');
    } catch (e) {
        console.error("Error formatting passing date from fields:", member, e);
        return null;
    }
};

// Month options for dropdown
const MONTHS = [
    { value: '', label: 'Month' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
];

// Generate day options (1-31)
const DAYS = [{ value: '', label: 'Day' }, ...Array.from({ length: 31 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))];

// Generate year options (current year back to 1900)
const currentYear = new Date().getFullYear();
const YEARS = [{ value: '', label: 'Year (optional)' }, ...Array.from({ length: currentYear - 1899 }, (_, i) => ({ value: String(currentYear - i), label: String(currentYear - i) }))];


function MemberDetail() {
    const { familyId, memberId } = useParams();
    const navigate = useNavigate();
    const { currentUser, isAdmin, userFidClaim, loadingAuth, userOrgId } = useAuth();

    // --- State ---
    const [family, setFamily] = useState(null);
    const [member, setMember] = useState(null);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Delete loading state
    const [deletingMember, setDeletingMember] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    // Deceased modal state
    const [showDeceasedModal, setShowDeceasedModal] = useState(false);
    const [deceasedLoading, setDeceasedLoading] = useState(false);
    const [passingMonth, setPassingMonth] = useState('');
    const [passingDay, setPassingDay] = useState('');
    const [passingYear, setPassingYear] = useState('');

    // --- Combined useEffect for all data fetching ---
    useEffect(() => {
        if (loadingAuth) return;

        if (!familyId || !memberId || !userOrgId) {
            setError("Required IDs (Family, Member, or Org) are missing.");
            setLoading(false);
            return;
        }

        let isMounted = true;

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const familyDocRef = doc(db, 'family', familyId);
                const memberDocRef = doc(db, 'member', memberId);
                const rolesQuery = query(collection(db, 'role'), where('memberId', '==', memberId), where('orgId', '==', userOrgId));

                const [familyDocSnapshot, memberDocSnapshot, rolesSnapshot] = await Promise.all([
                    getDoc(familyDocRef),
                    getDoc(memberDocRef),
                    getDocs(rolesQuery)
                ]);

                if (!isMounted) return;

                if (!familyDocSnapshot.exists()) throw new Error(`Associated family not found with ID: ${familyId}`);
                if (!memberDocSnapshot.exists()) throw new Error(`Member not found with ID: ${memberId}`);

                const familyData = { ...familyDocSnapshot.data(), id: familyDocSnapshot.id };
                const memberData = { ...memberDocSnapshot.data(), id: memberDocSnapshot.id };

                if (memberData.familyId !== familyId) {
                    throw new Error("Security check failed: This member does not belong to the specified family.");
                }

                const memberRoles = rolesSnapshot.docs.map(d => ({...d.data(), id: d.id}));
                memberRoles.sort((a,b) => (a.groupName || '').localeCompare(b.groupName || ''));

                setFamily(familyData);
                setMember(memberData);
                setRoles(memberRoles);

            } catch (err) {
                if (isMounted) {
                    console.error("Error fetching member details:", err);
                    setError(err.message || "An unexpected error occurred.");
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => { isMounted = false; };
    }, [familyId, memberId, loadingAuth, userOrgId]);


    // Handler: Delete Member
    const handleConfirmDeleteMember = async () => {
        setError(null);
        setDeletingMember(true);
        try {
            const result = await deleteMemberCallable({ memberId: member.id });
            if (result?.data?.success) {
                navigate(`/families/${familyId}`, { replace: true, state: { message: `${member.name || 'Member'} deleted successfully.` } });
            } else {
                setError(result?.data?.message || "Server denied the member deletion request.");
            }
        } catch (err) {
             setError(`Failed to delete member: ${err.message || "Network/Function error"}`);
        } finally {
            setDeletingMember(false);
        }
    };

    // Handler: Open deceased modal
    const handleOpenDeceasedModal = () => {
        setPassingMonth('');
        setPassingDay('');
        setPassingYear('');
        setShowDeceasedModal(true);
    };

    // Handler: Mark Member as Deceased
    const handleMarkDeceased = async () => {
        setError(null);
        setDeceasedLoading(true);

        // Validate: if any date field is provided, month and day are required
        if ((passingMonth || passingDay || passingYear) && (!passingMonth || !passingDay)) {
            setError("If providing a date of passing, both month and day are required.");
            setDeceasedLoading(false);
            return;
        }

        try {
            const result = await markMemberDeceasedCallable({
                memberId: member.id,
                passingMonth: passingMonth || null,
                passingDay: passingDay || null,
                passingYear: passingYear || null,
            });

            if (result?.data?.success) {
                // Update local state to reflect change
                setMember(prev => ({
                    ...prev,
                    status: 'deceased',
                    passingMonth: passingMonth || null,
                    passingDay: passingDay || null,
                    passingYear: passingYear || null,
                }));
                setShowDeceasedModal(false);
            } else {
                setError(result?.data?.message || "Failed to mark member as deceased.");
            }
        } catch (err) {
            setError(`Failed to mark member as deceased: ${err.message || "Network/Function error"}`);
        } finally {
            setDeceasedLoading(false);
        }
    };

    // Handler: Revert Member to Active
    const handleRevertToActive = async () => {
        if (!window.confirm(`Are you sure you want to revert ${member?.name || 'this member'} to active status?`)) return;

        setError(null);
        try {
            const result = await revertMemberToActiveCallable({ memberId: member.id });

            if (result?.data?.success) {
                // Update local state to reflect change
                setMember(prev => ({
                    ...prev,
                    status: 'active',
                    passingMonth: null,
                    passingDay: null,
                    passingYear: null,
                }));
            } else {
                setError(result?.data?.message || "Failed to revert member to active.");
            }
        } catch (err) {
            setError(`Failed to revert member status: ${err.message || "Network/Function error"}`);
        }
    };


    // --- Loading State ---
    if (loadingAuth || loading) {
        return (
            <div className="member-detail-page">
                <div className="member-detail-loading">
                    <div className="skeleton-header"></div>
                    <div className="skeleton-hero"></div>
                    <div className="skeleton-card"></div>
                </div>
            </div>
        );
    }

    // --- Not logged in ---
    if (!currentUser) {
        return (
            <div className="member-detail-page">
                <div className="member-detail-header">
                    <div className="container">
                        <div className="member-detail-header-content">
                            <div className="member-detail-header-left">
                                <h1 className="member-detail-title">Member Details</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <Alert variant="warning">
                        Please <Alert.Link as={Link} to="/login">log in</Alert.Link> to view member details.
                    </Alert>
                </div>
            </div>
        );
    }

    // --- Error or no data ---
    if (error || !family || !member) {
        return (
            <div className="member-detail-page">
                <div className="member-detail-header">
                    <div className="container">
                        <div className="member-detail-header-content">
                            <div className="member-detail-header-left">
                                <Link to="/families" className="member-back-btn">
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </Link>
                                <h1 className="member-detail-title">Member Details</h1>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="container">
                    <div className="member-detail-content">
                        <Alert variant="danger">
                            {error || "Member or Family data could not be loaded."}
                        </Alert>
                    </div>
                </div>
            </div>
        );
    }

    // --- Determine Permissions ---
    const canModify = isAdmin || (userFidClaim && userFidClaim === family.id);
    const birthday = formatBirthday(member);
    const passingDate = formatPassingDate(member);
    const isDeceased = member.status === 'deceased';

    // --- Render Page Content ---
    return (
        <div className="member-detail-page">
            {/* Page Header */}
            <div className="member-detail-header">
                <div className="container">
                    <div className="member-detail-header-content">
                        <div className="member-detail-header-left">
                            <Link to={`/families/${family.id}`} className="member-back-btn" title={`Back to ${family.surname} Family`}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </Link>
                            <div>
                                <h1 className="member-detail-title">{member.name}</h1>
                                <p className="member-detail-subtitle">
                                    <Link to={`/families/${family.id}`}>{family.surname} Family</Link>
                                </p>
                            </div>
                        </div>
                        {canModify && (
                            <div className="member-header-actions">
                                <Link
                                    to={`/families/${family.id}`}
                                    className="member-action-btn-header member-action-btn-primary"
                                    title={`Back to ${family.surname} Family`}
                                >
                                    <FontAwesomeIcon icon={faUsers} />
                                    <span>Family</span>
                                </Link>
                                <Link
                                    to={`/members/edit/${member.id}`}
                                    className="member-action-btn-header member-action-btn-warning"
                                    title={`Edit ${member.name}`}
                                >
                                    <FontAwesomeIcon icon={faPencilAlt} />
                                    <span>Edit</span>
                                </Link>
                                {isAdmin && !isDeceased && (
                                    <button
                                        className="member-action-btn-header member-action-btn-secondary"
                                        title={`Mark ${member.name} as Deceased`}
                                        onClick={handleOpenDeceasedModal}
                                    >
                                        <FontAwesomeIcon icon={faCross} />
                                        <span>Deceased</span>
                                    </button>
                                )}
                                {isAdmin && isDeceased && (
                                    <button
                                        className="member-action-btn-header member-action-btn-secondary"
                                        title={`Revert ${member.name} to Active`}
                                        onClick={handleRevertToActive}
                                    >
                                        <FontAwesomeIcon icon={faUsers} />
                                        <span>Revert</span>
                                    </button>
                                )}
                                <button
                                    className="member-action-btn-header member-action-btn-danger"
                                    title={`Delete ${member.name}`}
                                    onClick={() => setShowDeleteModal(true)}
                                    disabled={deletingMember}
                                >
                                    <FontAwesomeIcon icon={deletingMember ? faSpinner : faTrashAlt} spin={deletingMember} />
                                    <span>{deletingMember ? 'Deleting...' : 'Delete'}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="member-detail-content">
                    {/* Error Alert */}
                    {error && (
                        <Alert variant="danger" className="mb-3" onClose={() => setError(null)} dismissible>
                            {error}
                        </Alert>
                    )}

                    {/* Hero Section - Photo + Quick Info */}
                    <div className="member-hero" style={{ position: 'relative' }}>
                        {deletingMember && (
                            <div className="deleting-overlay">
                                <FontAwesomeIcon icon={faSpinner} spin className="deleting-overlay-spinner" />
                                <span className="deleting-overlay-text">Deleting member...</span>
                            </div>
                        )}
                        <div className="member-photo-container">
                            <div className="member-photo-wrapper">
                                <MemberPhotoAvatar
                                    src={member.photo}
                                    alt={member.name}
                                    size={200}
                                    cacheKey={member.imageUpdatedAt instanceof Timestamp ? member.imageUpdatedAt.toMillis().toString() : member.id}
                                />
                            </div>
                        </div>
                        <div className="member-quick-info">
                            <div className="member-name-row">
                                <h2 className="member-name-large">{member.name}</h2>
                                {isDeceased && (
                                    <span className="member-deceased-badge">Deceased</span>
                                )}
                            </div>
                            <Link to={`/families/${family.id}`} className="member-family-link">
                                <FontAwesomeIcon icon={faUsers} />
                                {family.surname} Family
                            </Link>

                            <div className="member-contact-grid">
                                {/* Birthday */}
                                {birthday && (
                                    <div className="member-contact-item">
                                        <div className="member-contact-icon member-contact-icon-birthday">
                                            <FontAwesomeIcon icon={faBirthdayCake} />
                                        </div>
                                        <div className="member-contact-content">
                                            <div className="member-contact-label">Birthday</div>
                                            <div className="member-contact-value">{birthday}</div>
                                        </div>
                                    </div>
                                )}

                                {/* Date of Passing (for deceased members) */}
                                {isDeceased && passingDate && (
                                    <div className="member-contact-item">
                                        <div className="member-contact-icon member-contact-icon-passing">
                                            <FontAwesomeIcon icon={faCross} />
                                        </div>
                                        <div className="member-contact-content">
                                            <div className="member-contact-label">Date of Passing</div>
                                            <div className="member-contact-value">{passingDate}</div>
                                        </div>
                                    </div>
                                )}

                                {/* Phone */}
                                {member.cellphone && (
                                    <div className="member-contact-item">
                                        <div className="member-contact-icon member-contact-icon-phone">
                                            <FontAwesomeIcon icon={faPhone} />
                                        </div>
                                        <div className="member-contact-content">
                                            <div className="member-contact-label">Cellphone</div>
                                            <div className="member-contact-value">
                                                <a href={`tel:${member.cellphone}`}>
                                                    <FormattedPhoneNumber phoneNumber={member.cellphone} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Email */}
                                {member.email && (
                                    <div className="member-contact-item">
                                        <div className="member-contact-icon member-contact-icon-email">
                                            <FontAwesomeIcon icon={faEnvelope} />
                                        </div>
                                        <div className="member-contact-content">
                                            <div className="member-contact-label">Email</div>
                                            <div className="member-contact-value">
                                                <a href={`mailto:${member.email}`}>{member.email}</a>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Group Memberships Section */}
                    {roles.length > 0 && (
                        <div className="member-groups-section">
                            <div className="member-groups-header">
                                <h3 className="member-groups-title">
                                    <FontAwesomeIcon icon={faLayerGroup} />
                                    Group Memberships
                                    <span className="member-groups-count">{roles.length}</span>
                                </h3>
                            </div>
                            <div className="member-groups-body">
                                {roles.map(role => (
                                    <div key={role.id} className="member-group-item">
                                        <div className="member-group-icon">
                                            <FontAwesomeIcon icon={faLayerGroup} />
                                        </div>
                                        <div className="member-group-info">
                                            <h4 className="member-group-name">
                                                <Link to={`/groups`}>{role.groupName || 'Unknown Group'}</Link>
                                            </h4>
                                            {role.label && (
                                                <p className="member-group-role">{role.label}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* History Section */}
                    {canModify && (
                        <HistorySection entityType="MEMBER" entityId={member.id} />
                    )}
                </div>
            </div>

            {/* Delete Member Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => !deletingMember && setShowDeleteModal(false)} centered className="delete-confirm-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FontAwesomeIcon icon={faTrashAlt} />
                        Delete Member
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="delete-modal-message">
                        Are you sure you want to permanently delete <strong>{member.name}</strong>?
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)} disabled={deletingMember}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDeleteMember} disabled={deletingMember}>
                        {deletingMember ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faTrashAlt} className="me-2" />
                                Delete
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Mark as Deceased Modal */}
            <Modal show={showDeceasedModal} onHide={() => setShowDeceasedModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Mark as Deceased</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">
                        Marking {member.name} as deceased will:
                    </p>
                    <ul className="text-muted small mb-4">
                        <li>Hide them from the main directory (admins can still view)</li>
                        <li>Exclude them from automated birthday/anniversary messages</li>
                        <li>Preserve their record for pastoral care and memorial purposes</li>
                    </ul>

                    <Form.Group className="mb-3">
                        <Form.Label className="text-muted small">Date of Passing (optional)</Form.Label>
                        <div className="d-flex gap-2">
                            <Form.Select
                                value={passingMonth}
                                onChange={(e) => setPassingMonth(e.target.value)}
                                size="sm"
                            >
                                {MONTHS.map(m => (
                                    <option key={m.value} value={m.value}>{m.label}</option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                value={passingDay}
                                onChange={(e) => setPassingDay(e.target.value)}
                                size="sm"
                            >
                                {DAYS.map(d => (
                                    <option key={d.value} value={d.value}>{d.label}</option>
                                ))}
                            </Form.Select>
                            <Form.Select
                                value={passingYear}
                                onChange={(e) => setPassingYear(e.target.value)}
                                size="sm"
                            >
                                {YEARS.map(y => (
                                    <option key={y.value} value={y.value}>{y.label}</option>
                                ))}
                            </Form.Select>
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDeceasedModal(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={handleMarkDeceased}
                        disabled={deceasedLoading}
                    >
                        {deceasedLoading ? 'Processing...' : 'Mark as Deceased'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default MemberDetail;
